/**
 * Growth of jitter-buffer delay above its recent minimum, per link.
 *
 * This is the only signal in the stack that observes decode-side latency. Every other trigger
 * reads network RTT, and the Android failure mode does not move network RTT: when a decoder
 * falls behind — thermal throttling, a software-decode fallback, a MediaCodec stall — frames
 * still arrive and still decode, just progressively later. RTT stays flat, `freezeCount` does
 * not increment because nothing is actually dropped, and `jitterBufferTarget` is pinned to 0
 * on mobile so the absolute delay stays small. Playout latency *growth* is what moves.
 *
 * Measured as growth above the window minimum, for the same reason RttFloorTracker measures
 * queueing above an RTT floor: the absolute value is dominated by a per-device baseline, and
 * only the excess is actionable.
 *
 * WARM-UP. A hardware decoder holds a fixed number of frames in flight; that depth is the
 * device's baseline, not lag. On the pinned (Android) path the sample IS that standing depth,
 * and a baseline is only trustworthy once a full window has been observed — before that, the
 * pipeline is still filling and every sample reads as "growth" over the near-empty start.
 * The field data: a guest 5s into a clean session reported 367–448ms of "growth", was
 * stepped down to 360p/30fps for the whole session, and never actually had a decode problem.
 * `warmupMs` holds the reading at null until the first sample for a key is that old.
 */

/** Sliding window the per-link jitter-buffer minimum is taken over. */
export const PLAYOUT_LATENCY_WINDOW_MS = 10_000;
/** Below this many samples the baseline is untrusted and no growth may be reported. */
export const PLAYOUT_LATENCY_MIN_SAMPLES = 5;
/**
 * Warm-up for the pinned-jitter-buffer (Android) path: no growth is reported until a key has
 * been observed for a full window, so the decoder's steady pipeline depth is in the baseline
 * before anything is compared against it.
 */
export const PLAYOUT_LATENCY_PINNED_WARMUP_MS = PLAYOUT_LATENCY_WINDOW_MS;

type PlayoutSample = {
  atMs: number;
  jbDelayMs: number;
};

export type PlayoutLatencyTrackerOptions = {
  windowMs?: number;
  /** Time a key must have been observed before growth is reported. Default 0 (none). */
  warmupMs?: number;
  nowMs?: () => number;
};

export class PlayoutLatencyTracker {
  private readonly samplesByKey = new Map<string, PlayoutSample[]>();
  private readonly firstSeenAtByKey = new Map<string, number>();
  private readonly windowMs: number;
  private readonly warmupMs: number;
  private readonly nowMs: () => number;

  constructor(options: PlayoutLatencyTrackerOptions = {}) {
    this.windowMs = options.windowMs ?? PLAYOUT_LATENCY_WINDOW_MS;
    this.warmupMs = options.warmupMs ?? 0;
    this.nowMs = options.nowMs ?? (() => Date.now());
  }

  /**
   * Record a sample and return growth above the window minimum, or null while the baseline
   * is untrusted.
   *
   * Failing closed on a thin window is deliberate: a baseline built from one or two samples
   * would treat the first reading as its own floor, so a buffer that was already deep at
   * connect would report zero growth forever.
   */
  public observe(key: string, jbDelayMs: number): number | null {
    if (!Number.isFinite(jbDelayMs) || jbDelayMs < 0) {
      return this.growthFor(key, jbDelayMs);
    }
    const now = this.nowMs();
    if (!this.firstSeenAtByKey.has(key)) this.firstSeenAtByKey.set(key, now);
    const samples = this.samplesByKey.get(key) ?? [];
    samples.push({ atMs: now, jbDelayMs });
    this.samplesByKey.set(key, this.withinWindow(samples, now));
    return this.growthFor(key, jbDelayMs);
  }

  public clearKey(key: string): void {
    this.samplesByKey.delete(key);
    this.firstSeenAtByKey.delete(key);
  }

  public clearAll(): void {
    this.samplesByKey.clear();
    this.firstSeenAtByKey.clear();
  }

  private growthFor(key: string, currentMs: number): number | null {
    if (!Number.isFinite(currentMs)) return null;
    const now = this.nowMs();
    if (!this.warmedUp(key, now)) return null;
    const samples = this.withinWindow(this.samplesByKey.get(key) ?? [], now);
    if (samples.length < PLAYOUT_LATENCY_MIN_SAMPLES) {
      return null;
    }
    const min = samples.reduce(
      (lowest, sample) => (sample.jbDelayMs < lowest ? sample.jbDelayMs : lowest),
      Number.POSITIVE_INFINITY,
    );
    if (!Number.isFinite(min)) return null;
    return Math.max(0, currentMs - min);
  }

  private warmedUp(key: string, nowMs: number): boolean {
    if (this.warmupMs <= 0) return true;
    const firstSeenAt = this.firstSeenAtByKey.get(key);
    return firstSeenAt !== undefined && nowMs - firstSeenAt >= this.warmupMs;
  }

  private withinWindow(samples: PlayoutSample[], nowMs: number): PlayoutSample[] {
    return samples.filter((sample) => nowMs - sample.atMs <= this.windowMs);
  }
}
